// CrashHistoryTable.tsx
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';

import { getBgColor } from '@/components/features/casino/crash/GameBox/utils';
import { CustomTypography } from '@/components/features/casino/crash/PlayerBox/styles';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { BoxChip } from '../../PreviourRoundResult/styles';
import {
  BottomIconWrapper,
  StyledTableCell,
  StyledTableHeadCell,
} from './styles';
import { crashLatest } from '@/apis/casino/crash';
import { getMultiplier } from '@/components/features/casino/crash/utils';

interface CrashHistoryTableProps {
  showDetail: (data: any) => void;
}

const PAGE_SIZE = 6;
export const CrashHistoryTable: React.FC<CrashHistoryTableProps> = ({
  showDetail,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handleOpenModal = (rowData: any) => {
    showDetail(rowData);
  };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['crashHistory', page],
    queryFn: async () => {
      const res: any = await crashLatest({ page: page, pageSize: PAGE_SIZE });
      if (res && res.status.code === '0') {
        setTotalPages(Math.ceil(res.data.totalNum / PAGE_SIZE));
        return res.data.games;
      } else {
        return [];
      }
    },
    keepPreviousData: true,
  });

  if (isError) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <div>Error: {error && error?.message}</div>;
  }

  const handleChangePage = (newPage: number) => {
    if (newPage >= 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <TableContainer component={Box}>
        <Table stickyHeader>
          <TableHead
            sx={{
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <TableRow>
              <StyledTableHeadCell align="left">
                {t('Time')}
              </StyledTableHeadCell>
              <StyledTableHeadCell align="center">
                {t('Multiplier')}
              </StyledTableHeadCell>
              <StyledTableHeadCell align="center">
                {t('Details')}
              </StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }, (_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))
              : data?.map((row) => (
                  <TableRow key={row.id}>
                    <StyledTableCell>{row.startTime}</StyledTableCell>
                    <StyledTableCell align="center">
                      <BoxChip
                        sx={{
                          background: getBgColor(row.result),
                          color: '#fff',
                        }}
                        label={`${getMultiplier(row.result)}`}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button
                        variant="plain"
                        sx={{ height: '32px' }}
                        onClick={() => handleOpenModal(row)}
                      >
                        <CustomTypography>{t('View')}</CustomTypography>
                      </Button>
                    </StyledTableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <BottomIconWrapper>
        <IconButton
          onClick={() => handleChangePage(page - 1)}
          disabled={page === 1}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={() => handleChangePage(page + 1)}
          disabled={page === totalPages}
        >
          <KeyboardArrowRight />
        </IconButton>
      </BottomIconWrapper>
    </>
  );
};

export default CrashHistoryTable;
